import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Empty, Flow, ui, type FlowActionState, type FlowStep, type FlowValues } from '../ui';
import type { VerifiablePlatform } from '@goodbuild/rules/contracts';
import { countryName } from '@goodbuild/rules/countries';
import { ApiError, useApi, useApiMutation } from '../api';
import { verifyChannel } from '../verify-channel';
import { COUNTRY_OPTIONS, Loading, Screen } from './shared';

/**
 * Twitch, YouTube and TikTok — named as their platforms spell themselves.
 *
 * Written out rather than run through `selectOptions`, which lowercases what it
 * is given: a creator picking where they publish should read `YouTube`, not
 * `youtube`. The values are still the enum's own, because that is what
 * `applyInput` parses and what the column stores.
 *
 * **Kick is gone from this screen** (#92). It is still a legal value of every
 * `channel_platform` column — a channel proved some other way must stay
 * representable — and it is not something anybody can apply under, because
 * applying now means proving the channel and there is no Kick OAuth application
 * open to us. See `VERIFIABLE_PLATFORMS`.
 */
const PLATFORM_NAMES: Record<VerifiablePlatform, string> = {
  twitch: 'Twitch',
  youtube: 'YouTube',
  tiktok: 'TikTok',
};

function platformName(value: string): string {
  return PLATFORM_NAMES[value as VerifiablePlatform] ?? value;
}

/** See `standingFor` in `apps/api/src/lib/roles`. */
type RoleStanding = 'none' | 'applied' | 'active' | 'suspended';

interface Me {
  userId: string;
  isAdmin: boolean;
  creator: { handle: string; displayName: string; level: number } | null;
  standing: { creator: RoleStanding; creatorHandle: string | null };
}

/**
 * Which application this URL is asking for.
 *
 * **The host used to be the only answer, and it is not one everywhere.** One
 * deployment serves `creators.goodbuild.cc`, `developers.goodbuild.cc` and
 * `app.goodbuild.cc`, and `resolveSite` says `store` for the third — so
 * `/onboarding` on the app's own host showed a studio registration whoever
 * asked for it. That was invisible while the only way in was a link in the
 * chrome of one of the two audience hosts, and stopped being so the moment a
 * link had to be written *at* the app from somewhere else.
 *
 * **There is one application left** (#144), so this can only answer `creator`
 * or null. It is kept rather than deleted because the distinction it draws is
 * still real: `/onboarding/creator` states what it is asking for, and bare
 * `/onboarding` does not. A second application added later goes here.
 */
function requestedRole(pathname: string): 'creator' | null {
  return pathname.startsWith('/onboarding/creator') ? 'creator' : null;
}

/**
 * Onboarding, for whichever audience the URL or the host names.
 *
 * One route and, since #144, one application: apply, then connect a payout
 * account. It was two — `/onboarding/creator` and `/onboarding/studio` — and
 * the studio half registered an organisation that would own a game we sold.
 *
 * **Applying to be a creator grants nothing.** Section 9: an administrator
 * reviews every creator, and this form says so rather than implying that
 * finishing it opens anything.
 *
 * The payout step is offered as soon as the application exists, before
 * approval, because a provider's own onboarding takes days for some countries and
 * there is nothing to gain by serialising the two waits.
 */
export function OnboardingRoute(): React.ReactElement {
  const query = useApi<Me>(['me'], '/me');
  const me = query.data;

  const path = typeof window === 'undefined' ? '/onboarding' : window.location.pathname;
  /*
    One application, so the fallback is no longer a fork (#144).

    The host used to decide between two: `creators.goodbuild.cc` meant the
    creator form and anything else meant a studio registration. There is one
    application left, so every route into this page shows it.
  */
  /*
    Read and deliberately discarded, so the call is still made.

    `requestedRole` is the one place that knows `/onboarding/creator` states
    what it is asking for and bare `/onboarding` does not, and there is one
    application left for it to answer with. Calling it keeps the seam alive for
    the second one: a `void` here is a line to change, where deleting the call
    would be a rule to rediscover.
  */
  void requestedRole(path);

  /*
   * Whether there is anything left to apply for.
   *
   * `me.creator` is what this asked before, and it is null until a creator is
   * *approved* — so somebody who applied a fortnight ago was shown the
   * application form a second time, and filling it in earned them a 409 saying
   * the account already has a profile. `standing` distinguishes the two, which
   * is the whole reason it exists.
   */
  const applied = me !== undefined && me.standing.creator !== 'none';

  /*
   * The one route whose audience its own URL only sometimes states.
   *
   * `/onboarding` alone belongs to neither side, so `audienceForPath` answers
   * null for it and the page says which it is showing. Left to the fallback it
   * would take the host's answer — `store` on `app.goodbuild.cc` — and a
   * creator application would be filled in on the players' ground.
   *
   * A constant since #144, where it used to be a fork: the other ground was the
   * developers' blue, for a studio registration that no longer exists.
   */
  return (
    <Screen title="Creator setup" query={query} space="creators">
      {applied ? <Payouts /> : <CreatorApplication />}
    </Screen>
  );
}

/**
 * Sending an application, in the shape a flow's action has to answer in.
 *
 * **Both of these were bare `<label>Studio name <input></label>` pairs** — no
 * classes, no panel, platform-default inputs on the default background — which
 * made the two forms that decide whether somebody joins this platform at all
 * the worst-looking screens in the product (#87). They are flows now, for the
 * reason `flow/types.ts` gives: they are filled in once, by somebody deciding
 * whether to trust us, and the address each one asks for can be checked at the
 * moment it is given rather than refused at the end.
 *
 * What did not change is anything the server sees. Same routes, same keys, same
 * `/me` invalidation — this is how the questions are asked and not what is
 * asked, and `apps/app/src/ui/test/onboarding-flows.test.ts` compares the two sides
 * to keep it that way.
 *
 * The success sentence is handed back as the action's `ok`, which is the flow's
 * own done screen. It is usually on screen for a moment: invalidating `/me`
 * flips `applied` above, and the route swaps the finished application for the
 * payout step, which is where somebody has something left to do.
 */
function useApply(path: string): (body: unknown) => Promise<FlowActionState> {
  const call = useApiMutation();
  const cache = useQueryClient();

  return async (body: unknown): Promise<FlowActionState> => {
    try {
      const result = await call<{ ok: string }>(path, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      await cache.invalidateQueries({ queryKey: ['me'] });
      return { error: null, ok: result.ok };
    } catch (caught) {
      return {
        error: caught instanceof ApiError ? caught.message : 'That did not work. Try again.',
        ok: null,
      };
    }
  };
}

/**
 * A creator application: prove the channel, then say where you live.
 *
 * **It used to be five questions and three of them were assertions** — a
 * display name, a handle and a channel address, all typed in and taken on
 * trust. The schema was honest about what that was worth: `applied_channel_url`
 * is *"a claim, not a verified channel"*, and somebody on staff had to open
 * every one of those URLs and decide whether the person in front of them was
 * who they said they were.
 *
 * #91, #92, #93 and #94 are one change to that, in the order the screens run:
 *
 *   - **#92.** Which platform, from the three we can actually verify. Not Kick,
 *     which has no OAuth application open to us — a card that begins a round
 *     trip and returns somebody else's error page is worse than no card.
 *   - **#93.** The screen that follows says what is about to be asked and why,
 *     before the button that asks it. Specifically that the account being
 *     connected is a *check*, not a way into GOODBUILD.
 *   - **#91.** The connect step, which is the whole application's centre of
 *     gravity: an OAuth round trip through the platform, run from the step's
 *     own button. See `FlowConnectStep`.
 *   - **#94.** There is no handle question and no name question, because both
 *     come back from the platform. Nothing here is editable, which is what the
 *     task asks for — and it is structural rather than a disabled input,
 *     because the endpoint reads the verification rather than the payload.
 *
 * Built inside the component rather than at module scope, and that is the one
 * thing about this list that is not a preference: the cards come from
 * `GET /v1/channels/platforms`, which is what this deployment holds credentials
 * for. A fresh clone has none and is shown a sentence rather than three cards
 * that cannot work.
 */
function creatorSteps(
  platforms: readonly { platform: string; label: string }[],
  connect: (values: FlowValues) => Promise<FlowValues>,
): readonly FlowStep[] {
  return [
    {
      /*
        Cards and no pre-picked one. The `<Select>` this replaced opened on
        `twitch`, so anybody who did not touch it applied as a Twitch streamer
        whether or not they are one — a default on a question with no ordinary
        answer is a wrong answer nobody had to type.
      */
      kind: 'cards',
      name: 'platform',
      question: 'Where do you publish?',
      help: 'Your main one. You can add the others later.',
      options: platforms.map((entry) => ({ value: entry.platform, label: entry.label })),
      display: (value) => platformName(value),
    },
    {
      /*
        #93, and it is the `help` rather than a separate screen.

        The task asks for a message once the platform is chosen, saying the
        social account will have to be linked so it can be verified — and that
        it is a check rather than a sign-in. A screen of its own with nothing to
        do on it would be a click to get past; this is the screen with the
        button on it, so the sentence is where the decision is.
      */
      kind: 'connect',
      name: 'verification',
      question: 'Connect the account, so we can check the channel is yours',
      help: 'You sign in over there and let us read your name. That is all it does. It never becomes how you sign in here, and it cannot post, upload or message as you.',
      connectLabel: (values) => `Connect ${platformName(values['platform'] ?? '')}`,
      busyLabel: (values) => `Waiting for ${platformName(values['platform'] ?? '')}`,
      againLabel: 'Connect a different account',
      connect,
      /*
        What it proved, and what it means here. Both, because they are two
        different facts: the channel is theirs on the platform, and the address
        is what they will be at on GOODBUILD — which nobody chose, which is the
        point of #94 and is worth seeing before the review screen rather than
        discovering afterwards.
      */
      display: (_value, values) =>
        `${values['verifiedName'] ?? ''} · goodbuild.cc/@${values['verifiedHandle'] ?? ''}`,
    },
    {
      kind: 'choice',
      name: 'countryCode',
      question: 'Where do you live?',
      help: 'It decides how we can pay you, which is why the list is short.',
      options: COUNTRY_OPTIONS,
      searchPlaceholder: 'Type to filter',
      // The server's own refusal, said before it can happen: `applyInput`
      // answers `GOODBUILD cannot onboard a creator from that country yet`.
      emptyHint:
        'Nothing matches. We cannot pay creators outside this list yet, so we cannot take you on from there.',
      display: (value) => countryName(value) ?? value,
    },
  ];
}

interface Platforms {
  platforms: { platform: string; label: string }[];
}

function CreatorApplication(): React.ReactElement {
  const apply = useApply('/apply/creator');

  /*
   * What this deployment can actually verify (#92).
   *
   * Asked rather than assumed, for the reason `socialProviders` gives about
   * sign-in buttons: a card with no client id behind it begins an OAuth round
   * trip and comes back with somebody else's error, which looks like our fault
   * and happens after the person has left the page.
   */
  const query = useApi<Platforms>(['channels', 'platforms'], '/channels/platforms');
  const platforms = query.data?.platforms ?? [];

  /**
   * One verification, in the shape a connect step has to answer in.
   *
   * Writes four values and only one of them is a step: `verification` is the
   * step's own answer, and the three beside it exist so the confirmation and
   * the review screen can say what was proved. `submitValues` emits a hidden
   * input per *step*, so the other three never reach the server — which is
   * exactly right, because the server reads them from the row the popup wrote
   * rather than from anything this form could put in a field.
   */
  const connect = useMemo(
    () =>
      async (values: FlowValues): Promise<FlowValues> => {
        const result = await verifyChannel(values['platform'] ?? '');
        // Thrown rather than returned: the engine shows a thrown message under
        // the question, which is where the button that failed is.
        if (!result.ok) throw new Error(result.message);

        return {
          verification: result.channel.handle,
          verifiedName: result.channel.displayName,
          verifiedHandle: result.channel.suggestedHandle,
          verifiedUrl: result.channel.channelUrl,
        };
      },
    [],
  );

  const steps = useMemo(() => creatorSteps(platforms, connect), [platforms, connect]);

  async function send(_previous: FlowActionState, form: FormData): Promise<FlowActionState> {
    const read = (name: string): string => String(form.get(name) ?? '').trim();

    /*
     * Two fields, and that is the whole payload (#94).
     *
     * The handle, the display name and the channel address are read on the
     * server out of `channel_verification`, keyed to the caller. Posting them
     * from here would be posting something nothing reads, and building the form
     * so that it *could* is how a disabled input ends up being the only thing
     * standing between somebody and a name they did not prove.
     */
    return apply({ platform: read('platform'), countryCode: read('countryCode') });
  }

  if (query.isPending) return <Loading />;

  if (platforms.length === 0) {
    /*
     * No credentials for any of the three, which is the state a fresh clone is
     * in. Said plainly rather than drawn as an empty card list: there is
     * nothing the person reading it can do, and the one thing worth telling
     * them is that it is us rather than them.
     */
    return (
      <Empty>
        We cannot take creator applications right now. Nothing about your account is affected.
      </Empty>
    );
  }

  return (
    <Flow
      steps={steps}
      action={send}
      submitLabel="Apply"
      reviewTitle="Before we read it"
      reviewHelp="Change anything here. Nothing is sent until you press the button."
      welcome={{
        eyebrow: 'For creators',
        title: 'Apply as a creator',
        body: [
          'Show our games on your channel and get a cut of every sale you bring in.',
          'First we check the channel is yours. You sign in over there, we read your name, and that becomes your name here. There is nothing to type.',
          'A person reads every application. Three screens, and nothing is sent until the last one.',
        ],
        startLabel: 'Start',
      }}
    />
  );
}
/*
  `isCompany`, `isIndividual`, `STUDIO_STEPS` and `StudioApplication` were here,
  and went with the studios (#144).

  Between them they were the registration: an individual or a registered
  business, a name, an address at `goodbuild.cc/dev/<slug>`, a country we can
  pay out from, and — for a company — the register entry an agreement would
  eventually be signed against. #89 had rewritten the whole flow so that a
  person shipping under their own name did not have to invent a company; #125
  then shut it, answering with `RegistrationClosed` and an address to write to,
  on the grounds that a closure is not a removal.

  This is the removal. There is no organisation to create and no publishing to
  apply for, so `POST /v1/apply/studio` is gone with the form.
*/

/**
 * Getting paid, which is one thing — the creators'.
 *
 * **There was a studio branch and it is gone** (#143, #144). It used to send
 * somebody to `/portal/payouts` to give a Revolut tag or bank details, because
 * #122 took studios off the provider rail and put them on a transfer sent by
 * hand; #143 removed the screen and left this branch saying there was nothing
 * to set up, and #144 removed the studios. There is nobody left for it to be
 * about, so the component takes no props at all.
 *
 * **A creator is paid by a provider**, and that half is unchanged in shape: it
 * ends by following the URL the provider returns rather than rendering it,
 * because onboarding links are single-use and expire in minutes — one baked
 * into the page is stale by the time anybody has read the sentence above it.
 *
 * It calls `POST /v1/payouts/onboarding`. It used to call `POST
 * /v1/payouts/connect`, which was Stripe Connect and went with Stripe; the
 * provider route is what every other surface already used.
 *
 * **This button does not work yet, and did not before either.** It sends no
 * `creatorProfileId` because this component has none to send — it takes no
 * props and nothing in this app exposes one — so the route answers 422. That
 * predates the Stripe removal and is not caused by it. Wiring it needs a source
 * for the id, which is a change to what this screen is handed rather than to
 * what it calls.
 */
function Payouts(): React.ReactElement {
  const call = useApiMutation();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function connect(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await call<{ url: string }>('/payouts/onboarding', {
        method: 'POST',
        body: JSON.stringify({ creatorProfileId: undefined as string | undefined }),
      });
      window.location.href = result.url;
    } catch (caught) {
      // Includes the honest "payouts are not configured on this deployment"
      // case, which is a sentence rather than a spinner that never resolves.
      setError(caught instanceof ApiError ? caught.message : 'Payout onboarding could not start.');
      setBusy(false);
    }
  }

  return (
    <section className={ui.stack ?? ''}>
      <h2 className="gb-subhead">Getting paid</h2>
      <p className="gb-body">
        We pay you through a payout company. You can set that up now — it does not wait for us to
        say yes, and in some countries it takes a few days.
      </p>

      <button type="button" onClick={() => void connect()} disabled={busy}>
        {busy ? 'Opening…' : 'Connect a payout account'}
      </button>

      {error ? (
        <p className="gb-body" role="alert">
          {error}
        </p>
      ) : null}

      <Empty>
        Nothing is lost while this is not set up. What you earn is kept, and paid once there is an
        account to pay it into.
      </Empty>
    </section>
  );
}
